import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogOut, Bell, X, User } from "lucide-react";

import BrandLogo from "../common/BrandLogo";

const DEFAULT_AVATAR = "https://customer-assets.emergentagent.com/job_67cdf678-c49e-46cf-85ed-5001e44caa60/artifacts/py4z7w22_user_logo.png";

export default function Header({ 
  isAuthed, 
  user, 
  credits,
  onLogout, 
  onOpenAccount,
  onOpenPricing,
  onSignIn,
  notifications = [],
  onMarkAsRead,
  onDeleteNotification,
  onMarkAllAsRead,
  onOpenPolicy
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Get user's profile picture or default
  const userAvatar = user?.picture || user?.avatar || DEFAULT_AVATAR;
  const userName = user?.name || "User";
  const userEmail = user?.email || "";
  const userCredits = credits ?? user?.credit_balance ?? 0;
  const subscriptionType = user?.subscription_active 
    ? (user?.plan === 'yearly' ? 'Yearly' : 'Monthly')
    : (user?.is_trial ? 'Trial' : 'Free');

  // Close notification dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
    }
    
    if (notificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationOpen]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    }
    
    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  const handleNotificationClick = (notification) => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const handleDeleteClick = (e, notificationId) => {
    e.stopPropagation();
    if (onDeleteNotification) {
      onDeleteNotification(notificationId);
    }
  };

  const handlePolicyClick = (policy) => {
    if (policy === 'pricing' && onOpenPricing) {
      onOpenPricing();
      setMobileMenuOpen(false);
      setProfileDropdownOpen(false);
      return;
    }
    if (onOpenPolicy) {
      onOpenPolicy(policy);
    }
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  const handleLogout = () => {
    setProfileDropdownOpen(false);
    onLogout();
  };

  const handleCreditsClick = () => {
    setProfileDropdownOpen(false);
    if (onOpenPricing) {
      onOpenPricing();
    }
  };

  return (
    <header className="header">
      <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
        <div className="brand">
          <BrandLogo size={32} className="brand-logo-img" />
          <Link to="/" className="brand-title">AI Music Maker</Link>
        </div>

        <nav className="topnav topnav-desktop">
          {!isAuthed ? (
            <>
              <button className="nav-link-btn" onClick={() => handlePolicyClick('howitworks')}>How It Works</button>
              <button className="nav-link-btn" onClick={() => handlePolicyClick('pricing')}>Pricing</button>
              <button className="nav-link-btn" onClick={() => handlePolicyClick('faq')}>FAQ</button>
              <button className="btn-outline" onClick={onSignIn}>Sign In</button>
            </>
          ) : (
            <>
              {/* Notification Bell */}
              <div className="notification-container" ref={notificationRef}>
                <button 
                  className={`btn-notification ${unreadCount > 0 ? 'has-unread' : ''}`}
                  onClick={() => setNotificationOpen(!notificationOpen)}
                  data-testid="notification-bell"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {notificationOpen && (
                  <div className="notification-dropdown">
                    <div className="notification-header">
                      <span className="notification-title">Notifications</span>
                      {notifications.length > 0 && unreadCount > 0 && (
                        <button 
                          className="notification-mark-all"
                          onClick={() => onMarkAllAsRead && onMarkAllAsRead()}
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                    
                    <div className="notification-list">
                      {notifications.length === 0 ? (
                        <div className="notification-empty">
                          No notifications yet
                        </div>
                      ) : (
                        notifications.map(notification => (
                          <div 
                            key={notification.id}
                            className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="notification-content">
                              <p className="notification-message">{notification.message}</p>
                              <span className="notification-time">{notification.timeAgo}</span>
                            </div>
                            <button 
                              className="notification-delete"
                              onClick={(e) => handleDeleteClick(e, notification.id)}
                              aria-label="Delete notification"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Profile Button & Dropdown */}
              <div className="profile-container" ref={profileRef}>
                <button 
                  className="profile-avatar-btn"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  data-testid="profile-avatar-btn"
                >
                  <img 
                    src={userAvatar} 
                    alt={userName}
                    className="profile-avatar-img"
                    onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                  />
                </button>

                {/* Profile Dropdown */}
                {profileDropdownOpen && (
                  <div className="profile-dropdown" data-testid="profile-dropdown">
                    {/* User Info Row */}
                    <div className="profile-user-row">
                      <div className="profile-user-info">
                        <span className="profile-user-name">{userName}</span>
                        <span className="profile-user-email">{userEmail}</span>
                      </div>
                      <img 
                        src={userAvatar} 
                        alt={userName}
                        className="profile-dropdown-avatar"
                        onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                      />
                    </div>

                    {/* Credits & Subscription Block */}
                    <div className="profile-credits-block">
                      <div className="profile-credits-left">
                        <button 
                          className="profile-subscription-badge"
                          onClick={handleCreditsClick}
                          data-testid="subscription-badge"
                        >
                          {subscriptionType}
                        </button>
                      </div>
                      <div className="profile-credits-right">
                        <button 
                          className="profile-credits-link"
                          onClick={handleCreditsClick}
                          data-testid="credits-link"
                        >
                          {userCredits} credits
                        </button>
                        <span className="profile-credits-note">credits are replenished monthly</span>
                      </div>
                    </div>

                    {/* Logout Button */}
                    <button 
                      className="profile-logout-btn"
                      onClick={handleLogout}
                      data-testid="profile-logout-btn"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>

                    {/* Navigation Links */}
                    <div className="profile-nav-links">
                      <button 
                        className="profile-nav-link"
                        onClick={() => handlePolicyClick('howitworks')}
                      >
                        How it works
                      </button>
                      <button 
                        className="profile-nav-link"
                        onClick={() => handlePolicyClick('pricing')}
                      >
                        Pricing
                      </button>
                      <button 
                        className="profile-nav-link"
                        onClick={() => handlePolicyClick('faq')}
                      >
                        FAQ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </nav>

        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-menu">
          {!isAuthed ? (
            <>
              <button className="mobile-nav-btn" onClick={() => handlePolicyClick('howitworks')}>How It Works</button>
              <button className="mobile-nav-btn" onClick={() => handlePolicyClick('pricing')}>Pricing</button>
              <button className="mobile-nav-btn" onClick={() => handlePolicyClick('faq')}>FAQ</button>
              <div className="mobile-menu-divider" />
              <button className="btn-primary btn-full-width" onClick={() => { setMobileMenuOpen(false); onSignIn(); }}>Sign In</button>
            </>
          ) : (
            <>
              {/* Mobile User Info */}
              <div className="mobile-user-row">
                <img 
                  src={userAvatar} 
                  alt={userName}
                  className="mobile-user-avatar"
                  onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                />
                <div className="mobile-user-info">
                  <span className="mobile-user-name">{userName}</span>
                  <span className="mobile-user-email">{userEmail}</span>
                </div>
              </div>
              
              <div className="mobile-credits-block">
                <span className="mobile-subscription-badge">{subscriptionType}</span>
                <button className="mobile-credits-link" onClick={() => { setMobileMenuOpen(false); handleCreditsClick(); }}>
                  {userCredits} credits
                </button>
              </div>
              
              <div className="mobile-menu-divider" />
              
              <button className="mobile-nav-btn" onClick={() => handlePolicyClick('howitworks')}>How It Works</button>
              <button className="mobile-nav-btn" onClick={() => handlePolicyClick('pricing')}>Pricing</button>
              <button className="mobile-nav-btn" onClick={() => handlePolicyClick('faq')}>FAQ</button>
              
              <div className="mobile-menu-divider" />
              
              <button className="btn-outline btn-full-width" onClick={() => { setMobileMenuOpen(false); onLogout(); }}>
                <LogOut size={16} className="inline mr-1"/>Logout
              </button>
              
              {unreadCount > 0 && (
                <div className="mobile-notification-indicator">
                  <Bell size={16} />
                  <span>{unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </header>
  );
}
